<script setup lang="ts">
import {dayjs} from "element-plus";
import {Section} from "@/types";
import {getDayOfWeek, getSectionSort} from "@/utils/common.ts";
import {computed} from "vue";

const props = defineProps<{
  index: number;
  course_status: number
  course_type: number
  is_fake: number
  section: Section
}>()

const liveDate = computed(() => {
  return dayjs(props.section?.live_date * 1000).format("YYYY年MM月DD日") +
      "(" +
      getDayOfWeek(props.section?.live_date * 1000) +
      ") " +
      (dayjs(props.section?.start_time * 1000).format("HH:mm") +
          "~" +
          dayjs(props.section?.end_time * 1000).format("HH:mm"))
})

const liveStatus = computed(() => {
  let status = "";
  if (props.is_fake === 1) {
    const curTime = Math.floor(Date.now() / 1000);
    if (curTime < props.section?.start_time) {
      status = "未开播";
    } else if (curTime <= props.section?.end_time) {
      status = "直播中";
    } else if (curTime > props.section?.end_time) {
      if (props.section.is_playback === 1) {
        status = calPlaybackStatus()
      } else {
        status = "直播已结束 | 未开启回放";
      }
    }
  } else {
    if (props.section?.status === 0) {
      status = "未开播";
    } else if (props.section?.status === 1) {
      if (props.section.is_playback === 1) {
        status = calPlaybackStatus()
      } else {
        status = "直播已结束 | 未开启回放";
      }
    } else if (props.section?.status === 2) {
      status = "直播中";
    } else if (props.section?.status === 3) {
      status = "录播";
    }
  }
  return status;
});

const calPlaybackStatus = (): string => {
  let status = "";
  if (props.section.view_start_time && props.section.view_end_time) {
    status = "直播已结束 | 回放时间：" +
        dayjs(props.section.view_start_time * 1000).format("YYYY-MM-DD HH:mm:ss")
        + "~" +
        dayjs(props.section.view_end_time * 1000).format("YYYY-MM-DD HH:mm:ss");
  } else if (props.section.view_start_time) {
    status = "直播已结束 | 回放时间：" +
        dayjs(props.section.view_start_time * 1000).format("YYYY-MM-DD HH:mm:ss")
        + " 开始"
  } else if (props.section.view_end_time) {
    status = "直播已结束 | 回放时间：" +
        dayjs(props.section.view_end_time * 1000).format("YYYY-MM-DD HH:mm:ss")
        + " 结束"
  } else {
    status = "直播已结束 | 回放时间无限制"
  }
  return status;
}

const canStartLive = computed(() => {
  if (props.course_status === 1 && props.course_type === 1) {
    if (props.is_fake === 1) {
      const curTime = Math.floor(Date.now() / 1000);
      return curTime <= props.section?.end_time && curTime >= props.section?.start_time;
    } else {
      return props.section.status === 2
    }
  }
})

const agingTime = computed(() => {
  if(props.section.is_playback == 1){
    if (props?.section.view_start_time && props?.section.view_end_time) {
      return (
              dayjs(props?.section.view_start_time * 1000).format("YYYY年MM月DD日 HH:mm") +
              "~" +
              dayjs(props?.section.view_end_time * 1000).format("YYYY年MM月DD日 HH:mm") +
              " 可观看"
      );
    }
    if (props?.section.view_start_time) {
      return dayjs(props?.section.view_start_time * 1000).format("YYYY年MM月DD日 HH:mm") + " 开始可观看";
    }
    if (props?.section.view_end_time) {
      return dayjs(props?.section.view_end_time * 1000).format("YYYY年MM月DD日 HH:mm") + " 之前可观看";
    }
  }else{
    return "无观看时效限制";
  }
});

const emit = defineEmits(['onEdit', 'onDelete', "startLive", "onChangeVideo", "studyList", "onUpDown", "onVideoClick"])

const studyList = (id: number, course_type: number) => {
  emit("studyList", id, course_type);
}

const onChangeVideo = (id: number, video_id: string) => {
  emit("onChangeVideo", id, video_id);
}

const onEdit = (id: number) => {
  emit("onEdit", id);
}

const onDelete = (id: number) => {
  emit("onDelete", id);
}

const startLive = (id: number) => {
  emit("startLive", id);
}

const onUp = (id: number) => {
  emit("onUpDown", 1, id);
}

const onDown = (id: number) => {
  emit("onUpDown", 0, id);
}

const sectionSort = computed(() => {
  return getSectionSort((props.index || 0) + 1)
})

const previewFile = (path: string) => {
  window.open(path, "_blank");
}

const onVideoClick = () => {
  if (props.course_type === 2) {
    if (props.section.video_id) {
      emit("onVideoClick", props.section.video_id, props.section.video_type)
    }
  }
}

function formatNumber(num: number): string {
  return num.toFixed(2); // 保留两位小数
}
</script>

<template>
  <div class="flex items-center mt-1.6875rem gap-.75rem">
    <div class="text-1.25em text-#FF6600CC font-bold">{{ sectionSort }}</div>
    <div class="text-1em text-#161616">{{ section.title }}</div>
  </div>
  <div class="section-info-container flex items-center bg-#f5f5f5 px-1rem pt-.75rem pb-.875rem mt-1.125rem rounded-md">
    <div class="flex-1">
      <div class="flex items-center gap-.75rem">
        <img src="@/assets/image/icon/icon-play.png" alt="play-icon" class="w-1.25rem h-1.25rem"/>
        <div class="text-#939393 text-.875em cursor-pointer" @click="onVideoClick">
          <template v-if="course_type === 1">
            直播时间：{{ liveDate }} | {{ liveStatus }}
          </template>
          <template v-else-if="course_type === 2 && section.video_id">
            视频已上传 <span v-if="section.video_type == 2">（保利威）</span> | {{ agingTime }} |
            <span v-if="section.is_try_study">试学 |
              <span v-if="section.try_study_second">试学 {{formatNumber(section.try_study_second/60)}} 分钟</span>
              <span v-else>整章节试学</span>
              </span>
            <span v-else>无试学</span>
<!--            视频已上传 {{ section.video_type == 2 ? "（保利威）" : "" }} | {{ agingTime }} | <span v-if="section.is_try_study">试学 | <span v-if="section.try_study_second">试学 {{formatNumber(section.try_study_second/3600)}} 分钟</span><span v-else>整章节试学</span> </span><span v-else>无试学</span>-->
          </template>
          <template v-else-if="course_type === 2">
            视频未上传
          </template>
        </div>
      </div>
      <div class="flex items-center mt-1rem gap-.75rem" v-if="section.ahead_data && section.ahead_data.name">
        <img src="@/assets/image/icon/icon-guide.png" alt="file-icon" class="w-1.25rem h-1.25rem"/>
        <div class="text-#939393 text-.875em cursor-pointer" @click="previewFile(section.ahead_data.path)">{{ section.ahead_data.name }}</div>
      </div>
      <div class="flex items-center mt-1rem gap-.75rem" v-if="section.pdf_data && section.pdf_data.name">
        <img src="@/assets/image/icon/icon-file.png" alt="file-icon" class="w-1.25rem h-1.25rem"/>
        <div class="text-#939393 text-.875em cursor-pointer" @click="previewFile(section.pdf_data.path)">{{ section.pdf_data.name }}</div>
      </div>
      <div class="flex items-center mt-1rem gap-.75rem"
           v-if="section.consolidate_data && section.consolidate_data.name">
        <img src="@/assets/image/icon/icon-consolidate.png" alt="file-icon" class="w-1.25rem h-1.25rem"/>
        <div class="text-#939393 text-.875em cursor-pointer" @click="previewFile(section.consolidate_data.path)">{{ section.consolidate_data.name }}</div>
      </div>
    </div>
    <div class="op-container flex items-center">
      <el-button type="primary" plain class="w-6rem h-2.5rem"
                 v-if="(course_type === 1 || course_type === 2)"
                 @click="studyList(section.id, course_type)">学习记录
      </el-button>
      <el-button type="primary" plain class="w-7.5rem h-2.5rem" v-if="course_type === 2 && section.video_type == 2">
        {{ section.video_id ? "当前视频不可更换": "上传视频" }}
      </el-button>
      <el-button type="primary" plain class="w-7.5rem h-2.5rem" v-else="course_type === 2 && section.video_type != 2"
                 @click="onChangeVideo(section.id, section.video_id)">
        {{ section.video_id ? "更换视频": "上传视频" }}
      </el-button>
      <el-button type="primary" plain class="w-6rem h-2.5rem" @click="onEdit(section.id)">编辑</el-button>
      <el-button type="danger" plain class="w-6rem h-2.5rem" v-if="course_status !== 1" @click="onDelete(section.id)">
        删除
      </el-button>
      <el-button type="primary" class="w-6rem h-2.5rem" @click="onUp(section.id)"
                 v-if="course_status == 1 &&(course_type === 1 || course_type === 2) && section.is_show === 0">上架
      </el-button>
      <el-button type="danger" plain class="w-6rem h-2.5rem" @click="onDown(section.id)"
                 v-if="course_status == 1 && (course_type === 1 || course_type === 2) && section.is_show === 1">下架
      </el-button>
      <el-button type="primary" class="w-6rem h-2.5rem" v-if="canStartLive" @click="startLive(section.id)">去上课
      </el-button>
    </div>
  </div>
</template>

<style scoped>
</style>
